/**
 * `<score-board>`: every scored round, and the running total, by team.
 */

import type { GameView, Team } from "@ryzun/haf-saloon-game-engine";
import type { TeamPresentation } from "../../server/protocol.js";
import { h } from "../dom.js";
import { getLeadingTeamIds, getRoundTotal, getTeamColor, getTeamName } from "../table-text.js";
import { BaseElement } from "./base-element.js";
import styles from "./score-board.css";

export interface ScoreModel {
    readonly view: GameView;
    readonly teamColors: readonly TeamPresentation[];
}

export class ScoreBoard extends BaseElement {
    #model: ScoreModel | null = null;

    constructor() {
        super(styles);
    }

    set model(value: ScoreModel | null) {
        this.#model = value;
        this.invalidate();
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            this.root.replaceChildren();
            return;
        }

        const { view } = model;
        const leaders = getLeadingTeamIds(view);

        const cell = (tag: "td" | "th", team: Team, ...content: readonly (Node | string)[]): HTMLElement => {
            const mine = view.players.some((player) => player.teamId === team.id && player.id === view.viewerId);
            const element = h(tag, tag === "th" ? { "scope": "col", "data-mine": mine } : { "data-mine": mine }, ...content);

            element.style.setProperty("--team", `var(--team-${getTeamColor(model.teamColors, team.id)})`);

            return element;
        };

        this.root.replaceChildren(h(
            "table",
            {},
            h("caption", {}, `Scores through round ${view.scores.length} of 4`),
            h("thead", {}, h(
                "tr",
                {},
                h("th", { scope: "col" }, "Round"),
                ...view.teams.map((team) => cell(
                    "th",
                    team,
                    h("span", { "class": "pip", "aria-hidden": "true" }),
                    getTeamName(view, team.id),
                    ...(leaders.includes(team.id) ? [h("span", { "class": "leader", "aria-label": "Leading" }, "★")] : [])
                ))
            )),
            h("tbody", {}, ...view.scores.map((scored) => h(
                "tr",
                {},
                h("th", { scope: "row" }, String(scored.round)),
                ...view.teams.map((team) => cell("td", team, String(getRoundTotal(view, scored.round, team.id) ?? "-")))
            ))),
            h("tfoot", {}, h(
                "tr",
                {},
                h("th", { scope: "row" }, "Total"),
                ...view.teams.map((team) => cell("td", team, String(team.score)))
            ))
        ));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "score-board": ScoreBoard;
    }
}

customElements.define("score-board", ScoreBoard);
